import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtGBP, fmtDate } from "@/lib/format";
import { Building2, ShieldQuestion, CalendarDays } from "lucide-react";

const SALE_STATUS_TONE = {
  "This week": "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  "Upcoming": "bg-amber-500/15 text-amber-400 border border-amber-500/30",
  "Past": "bg-slate-500/15 text-slate-400 border border-slate-600/40",
};

export default function Houses() {
  const { data = [] } = useQuery({
    queryKey: ["houses"],
    queryFn: async () => (await api.get("/houses")).data,
  });
  const { data: schedule = [] } = useQuery({
    queryKey: ["schedule"],
    queryFn: async () => (await api.get("/schedule")).data,
  });
  const scheduleFor = (house) => schedule.filter((s) => s.auction_house === house);

  return (
    <div>
      <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Source Coverage</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
        <Building2 className="w-7 h-7 text-amber-400" /> Auction Houses
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        {data.map((h) => (
          <div key={h.house} data-testid={`house-card-${h.house.replace(/[^a-z]/gi, "").toLowerCase()}`} className="al-card p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-slate-100">{h.house}</h3>
              <span className="al-chip border border-slate-700 text-slate-400">{Object.entries(h.by_location).map(([c, n]) => `${c} ${n}`).join(" · ")}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-center">
              <div className="min-w-0"><p className="mono-num text-lg text-slate-200 truncate">{h.lots}</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Lots</p></div>
              <div className="min-w-0"><p className="mono-num text-lg text-emerald-400 truncate">{h.sell_through_rate}%</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Sell-thru</p></div>
              <div className="min-w-0"><p className="mono-num text-lg text-amber-400 truncate">{fmtGBP(h.turnover_gbp)}</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Turnover</p></div>
              <div className="min-w-0"><p className="mono-num text-lg text-cyan-400 truncate">{fmtGBP(h.avg_price_gbp)}</p><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Avg</p></div>
            </div>
            {h.acquisition && (
              <div className="mt-5 p-3 rounded-md bg-amber-500/[0.06] border border-amber-500/20 flex gap-3">
                <ShieldQuestion className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-400">
                  <span className="text-amber-400/90 font-mono uppercase tracking-widest text-[10px]">Acquisition: {h.acquisition.method}</span>
                  <p className="mt-1">{h.acquisition.note}</p>
                </div>
              </div>
            )}

            <div className="mt-5" data-testid={`schedule-${h.house.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <p className="al-label flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-amber-400" /> Auction Calendar</p>
              <div className="space-y-1.5">
                {scheduleFor(h.house).length === 0 && <p className="text-xs text-slate-500">No scheduled sales.</p>}
                {scheduleFor(h.house).map((s) => (
                  <div key={s.schedule_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-800/50">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-200 truncate">{s.sale_name}</p>
                      <p className="text-[11px] text-slate-500">{s.auction_type} · {s.sale_location} · <span className="mono-num">{fmtDate(s.date)}</span></p>
                    </div>
                    <span className={`al-chip shrink-0 ${SALE_STATUS_TONE[s.status] || "bg-slate-500/15 text-slate-400"}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
